import React, { useState } from 'react';
import { InviteForm } from '../components/apprentices/InviteForm';
import { SignInLinkButton } from '../components/apprentices/SignInLinkButton';

export function ApprenticesPage({ profile, allProfiles, archivedApprentices = [], sites, getSiteName, onInvite, onGetLink, onDelete, onArchive, onRestore, onAssignTrainer, onUpdateUser, onAddSite, onUpdateSite, onDeleteSite }) {
  const [showInvite, setShowInvite] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Site management
  const [showAddSite, setShowAddSite] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteAddr, setNewSiteAddr] = useState('');
  const [editSiteId, setEditSiteId] = useState(null);
  const [editSiteForm, setEditSiteForm] = useState({});
  // Supervisor/Trainer assignment
  const [assigningId, setAssigningId] = useState(null);
  const [assignList, setAssignList] = useState([]);

  const apprentices = allProfiles.filter(p => p.role === 'apprentice' && !p.archived_at);
  const trainers = allProfiles.filter(p => p.role === 'trainer' && !p.archived_at);
  const supervisors = allProfiles.filter(p => p.role === 'supervisor' && !p.archived_at);

  // Password column. Anyone still showing a cross never got in — usually because
  // the invite email didn't arrive — so offer a link to send them directly.
  function PwdCell({ person }) {
    if (person.password_set) return <span style={{ color: 'var(--green)' }}>✓</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--red)' }} title="Hasn't set a password yet">✗</span>
        {profile.role === 'employer' && onGetLink && person.email && (
          <SignInLinkButton email={person.email} onGetLink={onGetLink} label="Get link" />
        )}
      </div>
    );
  }

  async function runArchive(id, archive) {
    setBusyId(id);
    try {
      await (archive ? onArchive(id) : onRestore(id));
      setConfirmArchive(null);
    } catch { /* toast shown by the caller */ }
    setBusyId(null);
  }

  function startEdit(person) {
    setEditingId(person.id);
    setEditForm({
      name: person.name || '',
      email: person.email || '',
      student_number: person.student_number || '',
      year: person.year || '',
      start_date: person.start_date || '',
      site_id: person.site_id || '',
    });
  }

  // Every one of these calls can fail. They report through the shared toast, so
  // here we only need to keep the row open when the save didn't land.
  async function saveEdit() {
    if (!editingId) return;
    const updates = { name: editForm.name.trim() };
    const person = allProfiles.find(p => p.id === editingId);

    if (person?.role === 'apprentice') {
      updates.student_number = editForm.student_number?.trim() || null;
      updates.year = editForm.year ? Number(editForm.year) : null;
      updates.start_date = editForm.start_date || null;
      updates.site_id = editForm.site_id || null;
    }

    try {
      await onUpdateUser(editingId, updates);
      setEditingId(null);
      setEditForm({});
    } catch { /* toast shown by the caller; leave the row open to retry */ }
  }

  function startAssign(person) {
    setAssigningId(person.id);
    setAssignList([...(person.assigned_to || [])]);
  }

  function toggleAssign(apprenticeId) {
    setAssignList(prev =>
      prev.includes(apprenticeId)
        ? prev.filter(id => id !== apprenticeId)
        : [...prev, apprenticeId]
    );
  }

  async function saveAssign() {
    if (!assigningId) return;
    try {
      await onAssignTrainer(assigningId, assignList);
      setAssigningId(null);
      setAssignList([]);
    } catch { /* toast shown by the caller */ }
  }

  // Get all supervisors assigned to an apprentice
  function getSupervisorsForApprentice(apprenticeId) {
    return supervisors.filter(s => (s.assigned_to || []).includes(apprenticeId));
  }

  function getCompletionDate(startDate) {
    if (!startDate) return null;
    const start = new Date(startDate);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 3);
    end.setDate(end.getDate() - 1);
    return end.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  async function handleAddSite() {
    if (!newSiteName.trim()) return;
    try {
      await onAddSite(newSiteName.trim(), newSiteAddr.trim());
      setNewSiteName(''); setNewSiteAddr(''); setShowAddSite(false);
    } catch { /* toast shown by the caller */ }
  }

  async function handleSaveSite() {
    if (!editSiteId) return;
    try {
      await onUpdateSite(editSiteId, { name: editSiteForm.name?.trim(), address: editSiteForm.address?.trim() || null });
      setEditSiteId(null); setEditSiteForm({});
    } catch { /* toast shown by the caller */ }
  }

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Manage People</div>
          <div className="page-sub">
            {apprentices.length} apprentice{apprentices.length !== 1 ? 's' : ''}
            {supervisors.length > 0 && `, ${supervisors.length} supervisor${supervisors.length !== 1 ? 's' : ''}`}
            , {trainers.length} trainer{trainers.length !== 1 ? 's' : ''}
            {(sites || []).length > 0 && ` · ${sites.length} site${sites.length !== 1 ? 's' : ''}`}
          </div>
        </div>
        {profile.role === 'employer' && (
          <button className="btn" onClick={() => setShowInvite(!showInvite)}>
            {showInvite ? 'Close' : '+ Invite User'}
          </button>
        )}
      </div>

      {showInvite && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-hdr"><span className="card-title">Invite New User</span></div>
          <div style={{ padding: 16 }}>
            <InviteForm onInvite={onInvite} onGetLink={onGetLink} />
          </div>
        </div>
      )}

      {/* ── SITES ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-hdr">
          <span className="card-title">Sites / Workshops</span>
          {profile.role === 'employer' && !showAddSite && (
            <button className="btn-sm" onClick={() => setShowAddSite(true)}>+ Add Site</button>
          )}
        </div>

        {showAddSite && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="fgrp" style={{ marginBottom: 0, flex: 1, minWidth: 150 }}>
              <label className="flbl">Site Name *</label>
              <input className="finp" value={newSiteName} onChange={e => setNewSiteName(e.target.value)} placeholder="e.g. Mornington Workshop" style={{ fontSize: 12, padding: '4px 8px' }} />
            </div>
            <div className="fgrp" style={{ marginBottom: 0, flex: 1, minWidth: 150 }}>
              <label className="flbl">Address</label>
              <input className="finp" value={newSiteAddr} onChange={e => setNewSiteAddr(e.target.value)} placeholder="Optional" style={{ fontSize: 12, padding: '4px 8px' }} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn xs green-outline" onClick={handleAddSite}>Save</button>
              <button className="btn xs outline" onClick={() => { setShowAddSite(false); setNewSiteName(''); setNewSiteAddr(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {(sites || []).length === 0 && !showAddSite ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
            No sites configured yet. Add sites to group apprentices by workshop location.
          </div>
        ) : (sites || []).length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Site Name</th>
                  <th>Address</th>
                  <th>Apprentices</th>
                  {profile.role === 'employer' && <th></th>}
                </tr>
              </thead>
              <tbody>
                {(sites || []).map(s => {
                  const siteApprentices = apprentices.filter(a => a.site_id === s.id);
                  return editSiteId === s.id ? (
                    <tr key={s.id} style={{ background: 'rgba(245,166,35,.05)' }}>
                      <td><input className="finp" value={editSiteForm.name || ''} onChange={e => setEditSiteForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 12, padding: '4px 8px' }} /></td>
                      <td><input className="finp" value={editSiteForm.address || ''} onChange={e => setEditSiteForm(f => ({ ...f, address: e.target.value }))} style={{ fontSize: 12, padding: '4px 8px' }} /></td>
                      <td style={{ fontSize: 12 }}>{siteApprentices.length}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn xs green-outline" onClick={handleSaveSite}>Save</button>
                          <button className="btn xs outline" onClick={() => setEditSiteId(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.name}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>{s.address || '—'}</td>
                      <td style={{ fontSize: 12 }}>{siteApprentices.length > 0 ? siteApprentices.map(a => a.name).join(', ') : '—'}</td>
                      {profile.role === 'employer' && (
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn xs outline" onClick={() => { setEditSiteId(s.id); setEditSiteForm({ name: s.name, address: s.address || '' }); }}>Edit</button>
                            <button className="btn xs danger-outline" onClick={() => onDeleteSite(s.id)}>Delete</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── APPRENTICES ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-hdr"><span className="card-title">Apprentices</span></div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Student #</th>
                <th>Site</th>
                <th>Supervisors</th>
                <th>Start Date</th>
                <th>Completion</th>
                <th>Pwd</th>
                {profile.role === 'employer' && <th></th>}
              </tr>
            </thead>
            <tbody>
              {apprentices.length === 0 ? (
                <tr><td colSpan={profile.role === 'employer' ? 9 : 8} style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>No apprentices yet</td></tr>
              ) : apprentices.map(a => (
                editingId === a.id ? (
                  <tr key={a.id} style={{ background: 'rgba(245,166,35,.05)' }}>
                    <td><input className="finp" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 12, padding: '4px 8px' }} /></td>
                    <td style={{ color: 'var(--muted)', fontSize: 11 }}>{a.email}</td>
                    <td><input className="finp" value={editForm.student_number} onChange={e => setEditForm(f => ({ ...f, student_number: e.target.value }))} style={{ fontSize: 12, padding: '4px 8px', width: 90 }} /></td>
                    <td>
                      <select className="fsel" value={editForm.site_id || ''} onChange={e => setEditForm(f => ({ ...f, site_id: e.target.value }))} style={{ fontSize: 11, padding: '3px 6px' }}>
                        <option value="">No site</option>
                        {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {getSupervisorsForApprentice(a.id).map(s => s.name).join(', ') || '—'}
                    </td>
                    <td><input className="finp" type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} style={{ fontSize: 11, padding: '3px 6px' }} /></td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>{editForm.start_date ? getCompletionDate(editForm.start_date) : '—'}</td>
                    <td><PwdCell person={a} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn xs green-outline" onClick={saveEdit}>Save</button>
                        <button className="btn xs outline" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600 }}>{a.name}</td>
                    <td style={{ color: 'var(--muted)', fontSize: 11 }}>{a.email}</td>
                    <td style={{ fontSize: 12 }}>{a.student_number || '—'}</td>
                    <td style={{ fontSize: 12 }}>{getSiteName(a.site_id) || '—'}</td>
                    <td style={{ fontSize: 11 }}>
                      {getSupervisorsForApprentice(a.id).map(s => s.name).join(', ') || '—'}
                    </td>
                    <td style={{ fontSize: 11 }}>{formatDate(a.start_date)}</td>
                    <td style={{ fontSize: 11 }}>{a.start_date ? getCompletionDate(a.start_date) : '—'}</td>
                    <td><PwdCell person={a} /></td>
                    {profile.role === 'employer' && (
                      <td>
                        {confirmArchive === a.id ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>
                              Keep records, switch off login?
                            </span>
                            <button className="btn xs green-outline" disabled={busyId === a.id} onClick={() => runArchive(a.id, true)}>
                              {busyId === a.id ? '…' : 'Archive'}
                            </button>
                            <button className="btn xs outline" onClick={() => setConfirmArchive(null)}>Cancel</button>
                          </div>
                        ) : confirmDelete === a.id ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10.5, color: 'var(--red)', fontWeight: 700 }}>
                              Erases all their evidence — no undo.
                            </span>
                            <button className="btn xs danger-outline" onClick={async () => { try { await onDelete(a.id); } catch { /* toast shown by the caller */ } setConfirmDelete(null); }}>Delete anyway</button>
                            <button className="btn xs outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn xs outline" onClick={() => startEdit(a)}>Edit</button>
                            <button className="btn xs outline" onClick={() => { setConfirmArchive(a.id); setConfirmDelete(null); }}>Archive</button>
                            <button className="btn xs danger-outline" onClick={() => { setConfirmDelete(a.id); setConfirmArchive(null); }}>Delete</button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ARCHIVED ── */}
      {archivedApprentices.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-hdr">
            <span className="card-title">
              Archived ({archivedApprentices.length})
            </span>
            <button className="btn-sm" onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? 'Hide' : 'Show'}
            </button>
          </div>
          {showArchived && (
            <>
              <div style={{ padding: '10px 16px 0', fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>
                Their evidence, sign-offs and trade school history are all still here and
                still available under Reports. They can't sign in until restored.
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Student #</th>
                      <th>Archived</th>
                      {profile.role === 'employer' && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {archivedApprentices.map(a => (
                      <tr key={a.id} style={{ opacity: 0.75 }}>
                        <td style={{ fontWeight: 600 }}>{a.name}</td>
                        <td style={{ color: 'var(--muted)', fontSize: 11 }}>{a.email}</td>
                        <td style={{ fontSize: 12 }}>{a.student_number || '—'}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>{formatDate(a.archived_at)}</td>
                        {profile.role === 'employer' && (
                          <td>
                            {confirmDelete === a.id ? (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 10.5, color: 'var(--red)', fontWeight: 700 }}>
                                  Erases all their evidence — no undo.
                                </span>
                                <button className="btn xs danger-outline" onClick={async () => { try { await onDelete(a.id); } catch { /* toast shown by the caller */ } setConfirmDelete(null); }}>Delete anyway</button>
                                <button className="btn xs outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="btn xs green-outline" disabled={busyId === a.id} onClick={() => runArchive(a.id, false)}>
                                  {busyId === a.id ? '…' : 'Restore'}
                                </button>
                                <button className="btn xs danger-outline" onClick={() => setConfirmDelete(a.id)}>Delete</button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── SUPERVISORS ── */}
      {(supervisors.length > 0 || profile.role === 'employer') && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-hdr"><span className="card-title">Supervisors</span></div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Assigned Apprentices</th>
                  <th>Pwd</th>
                  {profile.role === 'employer' && <th></th>}
                </tr>
              </thead>
              <tbody>
                {supervisors.length === 0 ? (
                  <tr><td colSpan={profile.role === 'employer' ? 5 : 4} style={{ textAlign: 'center', color: 'var(--muted)', padding: 20, fontSize: 12 }}>
                    No supervisors yet. Invite a supervisor to allow workshop leads to review evidence.
                  </td></tr>
                ) : supervisors.map(s => (
                  assigningId === s.id ? (
                    <tr key={s.id} style={{ background: 'rgba(245,166,35,.05)' }}>
                      <td style={{ fontWeight: 600 }}>{s.name}</td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{s.email}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {apprentices.map(a => {
                            const checked = assignList.includes(a.id);
                            return (
                              <label key={a.id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 11, cursor: 'pointer',
                                background: checked ? 'rgba(39,174,96,.1)' : 'rgba(255,255,255,.03)',
                                border: `1px solid ${checked ? 'var(--green)' : 'var(--border)'}`,
                                borderRadius: 4, padding: '3px 8px',
                              }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAssign(a.id)}
                                  style={{ margin: 0, accentColor: 'var(--green)' }}
                                />
                                {a.name}
                              </label>
                            );
                          })}
                        </div>
                      </td>
                      <td><PwdCell person={s} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn xs green-outline" onClick={saveAssign}>Save</button>
                          <button className="btn xs outline" onClick={() => setAssigningId(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  ) : editingId === s.id ? (
                    <tr key={s.id} style={{ background: 'rgba(245,166,35,.05)' }}>
                      <td><input className="finp" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 12, padding: '4px 8px' }} /></td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{s.email}</td>
                      <td style={{ fontSize: 12 }}>{(s.assigned_to || []).map(id => allProfiles.find(p => p.id === id)?.name || id).join(', ') || '—'}</td>
                      <td><PwdCell person={s} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn xs green-outline" onClick={saveEdit}>Save</button>
                          <button className="btn xs outline" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.name}</td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{s.email}</td>
                      <td style={{ fontSize: 12 }}>{(s.assigned_to || []).map(id => allProfiles.find(p => p.id === id)?.name || id).join(', ') || '—'}</td>
                      <td><PwdCell person={s} /></td>
                      {profile.role === 'employer' && (
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn xs outline" onClick={() => startAssign(s)}>Assign</button>
                            <button className="btn xs outline" onClick={() => startEdit(s)}>Edit</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TRAINERS ── */}
      <div className="card">
        <div className="card-hdr"><span className="card-title">Trainers</span></div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Assigned Apprentices</th>
                <th>Pwd</th>
                {profile.role === 'employer' && <th></th>}
              </tr>
            </thead>
            <tbody>
              {trainers.length === 0 ? (
                <tr><td colSpan={profile.role === 'employer' ? 5 : 4} style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>No trainers yet</td></tr>
              ) : trainers.map(t => (
                assigningId === t.id ? (
                  <tr key={t.id} style={{ background: 'rgba(245,166,35,.05)' }}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td style={{ color: 'var(--muted)', fontSize: 11 }}>{t.email}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {apprentices.map(a => {
                          const checked = assignList.includes(a.id);
                          return (
                            <label key={a.id} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 11, cursor: 'pointer',
                              background: checked ? 'rgba(39,174,96,.1)' : 'rgba(255,255,255,.03)',
                              border: `1px solid ${checked ? 'var(--green)' : 'var(--border)'}`,
                              borderRadius: 4, padding: '3px 8px',
                            }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAssign(a.id)}
                                style={{ margin: 0, accentColor: 'var(--green)' }}
                              />
                              {a.name}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td><PwdCell person={t} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn xs green-outline" onClick={saveAssign}>Save</button>
                        <button className="btn xs outline" onClick={() => setAssigningId(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : editingId === t.id ? (
                  <tr key={t.id} style={{ background: 'rgba(245,166,35,.05)' }}>
                    <td><input className="finp" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 12, padding: '4px 8px' }} /></td>
                    <td style={{ color: 'var(--muted)', fontSize: 11 }}>{t.email}</td>
                    <td style={{ fontSize: 12 }}>{(t.assigned_to || []).map(id => allProfiles.find(p => p.id === id)?.name || id).join(', ') || '—'}</td>
                    <td><PwdCell person={t} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn xs green-outline" onClick={saveEdit}>Save</button>
                        <button className="btn xs outline" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td style={{ color: 'var(--muted)', fontSize: 11 }}>{t.email}</td>
                    <td style={{ fontSize: 12 }}>{(t.assigned_to || []).map(id => allProfiles.find(p => p.id === id)?.name || id).join(', ') || '—'}</td>
                    <td><PwdCell person={t} /></td>
                    {profile.role === 'employer' && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn xs outline" onClick={() => startAssign(t)}>Assign</button>
                          <button className="btn xs outline" onClick={() => startEdit(t)}>Edit</button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
